import { useTranslation } from 'react-i18next';

import type { Order } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { refundFailureReason } from '../schemas/order-rules';

const STATUS_STYLES = {
  pending: 'bg-warning/20 text-warning-foreground border-transparent dark:bg-warning/25',
  succeeded: '',
  failed: 'bg-destructive/10 text-destructive border-transparent',
} as const;

/**
 * Money handed back, newest last, with what came back on hand. Card refunds settle at Stripe
 * after the fact, so one can sit in pending or come back failed. Only rendered once there is one.
 */
export function OrderRefundsCard({ order }: { order: Order }) {
  const { t } = useTranslation('orders');
  const format = useFormat();
  const titles = new Map(order.lines.map((line) => [line.id, line.title]));

  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('refunds.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col gap-3 text-sm" aria-label={t('refunds.title')}>
          {order.refunds.map((refund) => (
            <li key={refund.id} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium tabular-nums">{format.money(refund.amount)}</span>
                <span className="flex items-center gap-2">
                  {refund.restock ? (
                    <Badge variant="secondary">{t('refunds.restocked')}</Badge>
                  ) : null}
                  <Badge variant="outline" className={cn(STATUS_STYLES[refund.status])}>
                    {t(`refunds.status.${refund.status}`)}
                  </Badge>
                  <span
                    className="text-muted-foreground text-xs"
                    title={format.utc(refund.processedAt ?? refund.createdAt)}
                  >
                    {format.relative(refund.processedAt ?? refund.createdAt)}
                  </span>
                </span>
              </div>
              {refund.status === 'pending' ? (
                <p className="text-muted-foreground text-xs">{t('refunds.pendingHint')}</p>
              ) : null}
              {refund.status === 'failed' ? (
                <p className="text-destructive text-xs" role="note">
                  {t('refunds.failedReason', {
                    reason:
                      refundFailureReason(order.events, refund.id) ?? t('refunds.noFailureReason'),
                  })}
                </p>
              ) : null}
              {refund.reason ? <p className="text-muted-foreground">{refund.reason}</p> : null}
              {refund.lines.length > 0 ? (
                <ul className="text-muted-foreground text-xs">
                  {refund.lines.map((line) => (
                    <li key={line.orderLineId}>
                      {line.quantity} ×{' '}
                      {titles.get(line.orderLineId) ?? t('fulfillments.unknownLine')} ·{' '}
                      {format.money(line.amount)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
